// Como as encomendas se repartem pelas secções do painel.
//
// Extraído do `useOrders` para poder ser testado sem montar nada: são funções
// puras, e é aqui que vivem as regras que já falharam em silêncio uma vez.

// Quanto tempo uma encomenda concluída conta como "acabada agora mesmo".
//
// É a janela que o staff tem para tratar de uma reclamação que chega logo a
// seguir à entrega — faltou uma sobremesa, veio uma coisa trocada.
//
// **Isto já não decide em que secção a encomenda aparece.** Decidia, e enchia
// os pedidos ativos de trabalho já feito: a cozinha lia como tarefas oito
// cartões que só lá estavam à espera de um telefonema que quase nunca vem.
// Agora a encomenda cai para o histórico assim que fecha, e esta janela decide
// só a **cor** — dentro da hora o cartão sai com as cores normais, fora dela
// sai esbatido. É a mesma informação sem ocupar a coluna do trabalho.
//
// Uma hora, e não duas, porque é a janela que o cliente tem na app para
// reclamar de um problema depois de a encomenda passar a "Concluído". Passada
// essa hora a reclamação já não pode chegar pela app, e um cartão a cores
// estava a prometer uma coisa que já não acontece.
//
// Quando isto se parte não há erro nenhum: a encomenda de que o cliente acabou
// de falar ao telefone fica cinzenta no meio das outras.
export const RECENT_WINDOW_MS = 60 * 60 * 1000;

// Quanto tempo depois da hora marcada um pedido ainda aberto fica nos ativos.
//
// Passada esta hora o pedido sai para o histórico, esteja no estado que
// estiver. Na prática é um pedido que alguém se esqueceu de fechar — saiu ao
// balcão e ninguém carregou no botão — e que a cozinha já não consegue
// distinguir do trabalho de verdade: ao fim de uma noite eram cartões de há
// três horas a empurrar os da próxima meia hora para fora do ecrã. No histórico
// continua a cores e com o estado à vista, para alguém o fechar.
export const OVERDUE_AFTER_MS = 60 * 60 * 1000;

// Com quanto tempo de antecedência um "Novo Pedido" ainda por aceitar começa a
// piscar. Meia hora é o que uma francesinha precisa para sair a horas; um
// pedido que ninguém viu a vinte minutos da hora marcada já vai atrasado.
export const URGENT_NEW_MS = 30 * 60 * 1000;

// O que o histórico mostra sem se pedir mais nada.
export const HISTORY_WINDOW_MS = 48 * 60 * 60 * 1000;

const FECHADAS = ['completed', 'cancelled', 'refunded'];

export const isRefundRequested = (order) => order.refund_request_status === 'requested';

export const isClosed = (order) => FECHADAS.includes(order.app_status);

/**
 * A hora marcada pelo cliente: a de levantamento num pickup, a de entrega numa
 * entrega. `null` quando não há nenhuma que se leia.
 *
 * Tudo o que vem da app tem hora marcada, portanto não há fallback de
 * propósito: inventar uma hora a partir da chegada do pedido punha encomendas
 * antigas a saltar de secção por uma regra que não é a delas. As que não têm
 * hora ficam onde sempre estiveram.
 */
export const getOrderTime = (order) => {
  const orderType = order.tipo ? order.tipo.toLowerCase() : 'pickup';
  let bruto = null;
  if (orderType === 'pickup') bruto = order.pickup_time;
  if (orderType === 'delivery') bruto = order.delivery_time;
  if (!bruto) return null;
  const quando = new Date(bruto);
  // Uma data ilegível tratada como data partia a ordenação inteira (NaN em
  // todas as comparações). Mais vale "sem hora".
  return Number.isNaN(quando.getTime()) ? null : quando;
};

/**
 * Ainda aberto e já passou mais de uma hora da hora marcada?
 *
 * Ver o comentário do `OVERDUE_AFTER_MS`. Sem hora marcada nunca é: um pedido
 * antigo sem hora fica nos ativos, como sempre ficou.
 */
export const isOverdue = (order, now = Date.now()) => {
  if (isClosed(order)) return false;
  const hora = getOrderTime(order);
  if (!hora) return false;
  return now - hora.getTime() >= OVERDUE_AFTER_MS;
};

/**
 * Um "Novo Pedido" que ninguém aceitou e cuja hora está a chegar — ou já
 * passou.
 *
 * Só o `received`: a partir do "A Preparar" alguém já está a tratar dele, e
 * um cartão a piscar por cada francesinha no forno ensinava a cozinha a não
 * olhar para o piscar.
 */
export const isUrgentNew = (order, now = Date.now()) => {
  if (order.app_status !== 'received') return false;
  const hora = getOrderTime(order);
  if (!hora) return false;
  return hora.getTime() - now < URGENT_NEW_MS;
};

/**
 * A hora a que a encomenda entrou no histórico, em milissegundos.
 *
 * Para as fechadas é a hora a que fecharam. Para as atrasadas ainda abertas é
 * a hora a que saíram dos ativos — a hora marcada mais a hora de tolerância.
 * Com uma medida só, o histórico ordena-se de uma vez e o que acabou de
 * chegar lá fica sempre no topo, venha por que caminho vier. `null` quando
 * não está no histórico ou não se consegue dizer quando entrou.
 */
export const historyEnteredAt = (order, now = Date.now()) => {
  if (isClosed(order)) return closedAt(order);
  if (isOverdue(order, now)) return getOrderTime(order).getTime() + OVERDUE_AFTER_MS;
  return null;
};

/**
 * Quando é que a encomenda fechou, em milissegundos.
 *
 * `status_updated_at` é a hora real da mudança de estado. Nas encomendas
 * anteriores a esse campo usa-se a chegada do webhook: não é exato, mas é da
 * mesma tarde, e a alternativa era deixá-las cair do histórico para sempre.
 * `null` quando nenhuma das duas se lê.
 */
export const closedAt = (order) => {
  const bruto = order.status_updated_at || order.date_webhook_received || order.date_created;
  if (!bruto) return null;
  const quando = new Date(bruto).getTime();
  return Number.isNaN(quando) ? null : quando;
};

/**
 * Fechou há menos de uma hora?
 *
 * Só para a cor do cartão — ver o comentário do `RECENT_WINDOW_MS`. Exige o
 * `status_updated_at` de propósito: sem a hora real da mudança de estado não se
 * inventa uma janela de reclamação a partir da hora de chegada do pedido.
 */
export const isRecentlyClosed = (order, now = Date.now()) => {
  if (!isClosed(order)) return false;
  if (!order.status_updated_at) return false;
  const quando = new Date(order.status_updated_at).getTime();
  if (Number.isNaN(quando)) return false;
  return now - quando < RECENT_WINDOW_MS;
};

/** Os pedidos de reembolso por tratar. Sem limite: nenhum se pode perder. */
export const selectRefundRequests = (orders) => orders.filter(isRefundRequested);

/**
 * Os pedidos de reembolso que não estavam na leitura anterior — os que merecem
 * som.
 *
 * `anteriores` a `null` é a primeira leitura: não toca nada. Quem abre o painel
 * vê a secção a piscar; um alarme por cada pedido que já lá estava ensinava a
 * ignorar o som no primeiro dia.
 */
export const newRefundRequestIds = (anteriores, pedidos) => {
  if (anteriores === null) return [];
  return pedidos
    .filter(isRefundRequested)
    .map((o) => o.order_id)
    .filter((id) => !anteriores.has(id));
};

/**
 * O que está a acontecer agora: só o que ainda tem trabalho pela frente.
 *
 * Uma encomenda fechada sai daqui no momento em que fecha, mesmo que tenha
 * fechado há um minuto. Continua à vista — no topo do histórico, e a cores.
 * Uma aberta sai quando passa uma hora da hora marcada (`isOverdue`), pelo
 * mesmo caminho.
 *
 * `now` vem do relógio do `useOrders`, e não do `Date.now()` lá dentro: é o
 * relógio que faz o pedido mudar de secção à hora certa sem esperar pelo
 * próximo refresh.
 */
export const selectActive = (orders, now = Date.now()) =>
  orders.filter((o) => !isClosed(o) && !isRefundRequested(o) && !isOverdue(o, now));

/**
 * Pesquisa livre sobre uma encomenda: número, nome, telefone e email.
 *
 * São as coisas que quem está ao telefone tem à mão. O número vem sem
 * cardinal, para "1234" e "#1234" darem os dois. Vale para todas as secções
 * (ver o campo de pesquisa nos Filtros), e não só para o histórico.
 */
export const matchesSearch = (order, termo) => {
  const procura = (termo || '').trim().toLowerCase().replace(/^#/, '');
  if (!procura) return true;

  const campos = [
    String(order.order_id ?? ''),
    order.customer_name || '',
    order.billing?.phone || order.customer_phone || '',
    order.billing?.email || '',
  ];

  return campos.some((campo) => campo.toLowerCase().includes(procura));
};

/**
 * O histórico: tudo o que já não está ativo, dentro da janela pedida — as
 * fechadas e as atrasadas ainda abertas.
 *
 * Ordenado pela hora a que entrou no histórico (`historyEnteredAt`), do mais
 * recente para o mais antigo — e não pela hora de levantamento, que era o que
 * fazia a encomenda das 19h de ontem aparecer à frente da que acabou de sair.
 * A janela e a pesquisa usam a mesma hora, para uma atrasada ter as mesmas
 * regras que uma fechada.
 *
 * `windowMs: null` significa tudo. Não há corte por contagem: o que limita o
 * que vem da rede é a janela, e o que limita o que se desenha é a página.
 * Misturar as duas coisas foi o que fez o histórico mostrar oito e mais nada.
 */
export const selectHistory = (orders, { now = Date.now(), windowMs = HISTORY_WINDOW_MS, search = '' } = {}) => {
  const limite = windowMs === null ? null : now - windowMs;

  return orders
    .filter((o) => {
      if (isRefundRequested(o)) return false;
      if (!isClosed(o) && !isOverdue(o, now)) return false;
      if (limite !== null) {
        const quando = historyEnteredAt(o, now);
        // Sem hora legível não se consegue dizer se cabe na janela. Fica de
        // fora das janelas com limite e aparece no "Todos", que é onde quem a
        // procura a vai encontrar.
        if (quando === null || quando < limite) return false;
      }
      return matchesSearch(o, search);
    })
    .sort((a, b) => (historyEnteredAt(b, now) ?? 0) - (historyEnteredAt(a, now) ?? 0));
};

/** Junta duas listas de encomendas por `order_id`, sem repetir. */
export const mergeById = (a, b) => {
  const porId = new Map();
  [...a, ...b].forEach((order) => porId.set(order.order_id, order));
  return [...porId.values()];
};
